// Device-class helpers. Two distinct concerns share the same three labels:
//
//   - detectDeviceClass() is viewport-only and drives LAYOUT (the
//     data-device-class attribute, responsive CSS, ViewportGate). Shrinking a
//     desktop window to phone width should switch to the phone layout, so this
//     follows the viewport, not the device.
//   - authDeviceClass() mirrors the server's authoritative classifier
//     (internal/deviceclass, ) and drives AUTH: the X-Device-Class header
//     sent on refresh. authViewportHint() is the viewportWidth hint sent at
//     sign-in. Both are User-Agent-primary and resize-invariant so the class a
//     refresh presents agrees with the class the server bound at sign-in; if it
//     didn't, every refresh on an unchanged device would spuriously trip the
//     server's device-class-switch check and force re-auth.
//
// The pre-paint version of detectDeviceClass lives inline in index.html.

export type DeviceClass = 'phone' | 'tablet' | 'desktop';

// The LAYOUT breakpoints, and they must read the same way the CSS does.
//
// CSS says `min-width: 768px`, i.e. desk chrome starts AT 768. These used to say
// `w <= 768 → phone`, i.e. 768 is still phone — so at exactly 768px (iPad portrait,
// and a common split-screen width) the user got desk chrome over a capture body,
// with an Admin link leading to a "not available on this surface" notice.
//
// Hence strict `<`: the boundary value belongs to the WIDER class, on both sides.
// CSS cannot import these without a build step, so the contract is a comment on
// each side plus the boundary test in viewport.test.ts. If you change a number
// here, change it in nav.css, shell.css, surface.css and health.css — and in the
// pre-paint copy in index.html, which must agree or the first paint flashes the
// wrong class.
const PHONE_MAX = 768; // must match `min-width: 768px` in nav.css et al.
const TABLET_MAX = 1024; // must match `min-width: 1024px` in shell.css / surface.css

// Viewport-derived, and LAYOUT-only: this feeds setRootDeviceClass and, through it,
// the capture/desk surface. It is NOT the session's device_class — that comes from
// authDeviceClass below, which is User-Agent-primary. A resize re-surfaces the UI
// and never changes capabilities (see the header comment in utils/surface.ts), so
// moving this boundary cannot widen a capability ceiling.
export function detectDeviceClass(): DeviceClass {
  const w = window.innerWidth;
  if (w < PHONE_MAX) return 'phone';
  if (w < TABLET_MAX) return 'tablet';
  return 'desktop';
}

// authViewportHint is the viewportWidth hint the SPA sends at sign-in, and the
// hint authDeviceClass re-derives the refresh-time class from. It must be a
// function of the DEVICE, never of the window: the server binds the class it
// derives from (User-Agent, hint) at sign-in and refuses any refresh presenting a
// different class (401 DEVICE_CLASS_CHANGED, which also clears the refresh
// cookie). It used to be window.innerWidth, so a Mac user who signed in wide and
// then narrowed the window to 1024 px or less — split screen, a half-width
// window, a smaller display — was signed out at the next refresh, and the reverse
// did the same (issue 718).
//
// The one thing the hint exists for is unmasking iPadOS Safari, which reports a
// macOS User-Agent. Width was a proxy for that; touch capability is the signal
// itself and does not change when a window resizes. No Mac browser reports touch
// points, and iPadOS Safari reports five. So:
//
//   - a touch-capable macOS User-Agent (iPadOS in desktop mode) sends a
//     tablet-band width — the real width clamped to TABLET_MAX, so an iPad Pro in
//     landscape (1180, 1366) classifies tablet too instead of desktop, and
//     rotating it no longer flips its class either;
//   - everything else sends 0, "no hint": the User-Agent alone decides.
//
// Every value returned is 0 or inside (0, TABLET_MAX], and the server's Classify
// can only narrow on a hint, so this can never widen a class. The same rows are
// pinned on both sides by internal/deviceclass/testdata/spa_hint_parity.json.
export function authViewportHint(
  userAgent: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints ?? 0,
  viewportWidth: number = window.innerWidth,
): number {
  const ua = userAgent.toLowerCase();
  const isMac = ua.includes('macintosh') || ua.includes('mac os x');
  if (!isMac || !(maxTouchPoints > 1)) return 0;
  return viewportWidth > 0 ? Math.min(viewportWidth, TABLET_MAX) : TABLET_MAX;
}

// authDeviceClass mirrors the Go classifier deviceclass.Classify exactly:
// User-Agent decides phone/tablet/desktop; the viewport hint is advisory and
// only narrows desktop→tablet for an iPadOS Safari reporting a macOS UA. Keep
// this in lockstep with internal/deviceclass/classify.go — they must produce
// the same class for the same (UA, hint) or refresh will force re-auth. The
// default hint is authViewportHint(), the same value sign-in sends, so the class
// presented on refresh is the class the server bound.
export function authDeviceClass(
  userAgent: string = navigator.userAgent,
  viewportHint: number = authViewportHint(userAgent),
): DeviceClass {
  const ua = userAgent.trim().toLowerCase();
  if (ua === '') return 'phone';
  // Headless / bot agents → most restrictive (before the desktop-OS branch).
  if (/headless|phantomjs|bot|crawler|spider|slurp/.test(ua)) return 'phone';
  // Hard phone signals — viewport never consulted, so a spoofed width can't
  // widen a phone.
  if (ua.includes('iphone') || ua.includes('ipod')) return 'phone';
  if (ua.includes('android') && ua.includes('mobile')) return 'phone';
  if (/windows phone|iemobile|blackberry|bb10|opera mini|opera mobi/.test(ua)) return 'phone';
  // Hard tablet signals (iPad before the macOS branch — its UA says "Mac OS X").
  if (ua.includes('ipad')) return 'tablet';
  if (ua.includes('android')) return 'tablet';
  if (/silk|kindle|playbook/.test(ua)) return 'tablet';
  // Desktop OS, with the iPadOS-as-macOS narrowing (desktop→tablet only).
  if (/macintosh|mac os x|windows nt|cros|x11|linux/.test(ua)) {
    const isMac = ua.includes('macintosh') || ua.includes('mac os x');
    // NOTE: `<=`, deliberately, and NOT aligned with the strict `<` above. This is
    // the auth-side classifier and it must equal deviceclass.Classify in
    // internal/deviceclass/classify.go, which reads
    // `viewportHint <= tabletMaxWidth`. Making these disagree would classify a Mac
    // at exactly 1024 differently on each side, and the mismatch trips the server's
    // device-class-switch check — forcing re-auth on every refresh. This boundary
    // is about narrowing iPadOS-reporting-macOS, not about the layout surface.
    if (isMac && viewportHint > 0 && viewportHint <= TABLET_MAX) return 'tablet';
    return 'desktop';
  }
  return 'phone';
}

export function setRootDeviceClass(): DeviceClass {
  const dc = detectDeviceClass();
  document.documentElement.setAttribute('data-device-class', dc);
  return dc;
}

type Listener = (dc: DeviceClass) => void;

const listeners = new Set<Listener>();
let current: DeviceClass = detectDeviceClass();
let resizeTimer: number | null = null;

window.addEventListener('resize', () => {
  if (resizeTimer !== null) window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    const next = detectDeviceClass();
    if (next !== current) {
      current = next;
      document.documentElement.setAttribute('data-device-class', next);
      listeners.forEach((fn) => fn(next));
    }
  }, 150);
});

export function onDeviceClassChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getDeviceClass(): DeviceClass {
  return current;
}
